/**
 * finance-core — Bộ tổng hợp chuỗi tháng dùng chung (GA4, quyết định 18/07/2026)
 *
 * Nguồn chân lý duy nhất cho "tổng thu/chi theo kỳ" của các tab Phân tích —
 * trước đây 7 code path tự tính, đã lệch nhau ở hoàn tiền. Quy ước:
 * - Chi tiêu dùng CHIỀU CÓ DẤU: hoàn tiền (isRefund) mang dấu âm
 * - Bucket theo field year/month của bản ghi (chuẩn bucketing của app)
 * - Tháng hiện tại đánh dấu isPartial — người tiêu thụ phải hiển thị nhãn
 *   "chưa kết thúc" và loại khỏi hồi quy/trung bình trượt (GA3)
 */

export interface TxRecord {
  year?: number;
  month?: number;
  amount?: number;
  isRefund?: boolean;
  /** Giải ngân khoản vay: tiền vào ví nhưng KHÔNG phải thu nhập (loại khỏi mọi tổng thu) */
  isLoanProceeds?: boolean;
  transactionDate?: { toDate?: () => Date } | Date | string | null;
}

export interface MonthPoint {
  year: number;
  month: number;
  label: string;      // "T7/2026"
  income: number;
  expense: number;    // đã có dấu (hoàn tiền trừ đi)
  net: number;
  isPartial: boolean; // tháng hiện tại, chưa kết thúc
}

/** Thu nhập thật: loại tiền vay giải ngân (nợ tăng, không phải kiếm được tiền). */
export const isIncome = (r: TxRecord): boolean => !r?.isLoanProceeds;

/** Chiều "chi tiêu" chuẩn hoá — trùng quy ước signedExpenseAmount của expenseService */
export const signedAmount = (e: TxRecord): number =>
  (e?.isRefund ? -1 : 1) * (Number(e?.amount) || 0);

const txDate = (r: TxRecord): Date | null => {
  const v = r.transactionDate;
  if (!v) return null;
  const d = typeof v === 'object' && 'toDate' in v && v.toDate ? v.toDate() : new Date(v as string | Date);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Chuỗi N tháng gần nhất (cũ → mới), gồm tháng hiện tại (đánh dấu isPartial).
 */
export const monthlySeries = (
  expenses: TxRecord[],
  incomes: TxRecord[],
  monthsBack: number,
  now: Date = new Date()
): MonthPoint[] => {
  const points: MonthPoint[] = [];
  const curY = now.getFullYear();
  const curM = now.getMonth() + 1;

  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(curY, curM - 1 - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;

    const income = incomes
      .filter(r => isIncome(r) && r.year === y && r.month === m)
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const expense = expenses
      .filter(r => r.year === y && r.month === m)
      .reduce((s, r) => s + signedAmount(r), 0);

    points.push({
      year: y,
      month: m,
      label: `T${m}/${y}`,
      income,
      expense,
      net: income - expense,
      isPartial: y === curY && m === curM
    });
  }
  return points;
};

/**
 * Tổng theo bucket year/month của bản ghi (chuẩn bucketing của app).
 * months bỏ qua = cả năm; một số = đúng tháng đó; mảng = các tháng liệt kê.
 * Dùng cho so sánh kỳ-đã-kết-thúc (tháng đủ vs tháng đủ, N tháng đầu hai năm).
 */
export const bucketTotal = (
  items: TxRecord[],
  year: number,
  months?: number | number[],
  useSigned = false
): number => {
  const set = months == null ? null : new Set(Array.isArray(months) ? months : [months]);
  return items.reduce((sum, r) => {
    if (r.year !== year) return sum;
    if (set && !set.has(r.month as number)) return sum;
    return sum + (useSigned ? signedAmount(r) : (Number(r.amount) || 0));
  }, 0);
};

/**
 * GA3 — So sánh CÙNG-SỐ-NGÀY (phản biện PB3: không ngoại suy run-rate).
 * Trả về tổng của [periodStart, periodStart + elapsedDays) — lọc theo
 * transactionDate thật, không theo bucket tháng.
 */
export const sumSameDays = (
  items: TxRecord[],
  periodStart: Date,
  elapsedDays: number,
  useSigned = false
): number => {
  const start = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate());
  const end = new Date(start.getTime() + elapsedDays * 86400000);
  return items.reduce((sum, r) => {
    const d = txDate(r);
    if (!d || d < start || d >= end) return sum;
    return sum + (useSigned ? signedAmount(r) : (Number(r.amount) || 0));
  }, 0);
};

/**
 * So sánh kỳ hiện tại vs kỳ trước theo CÙNG số ngày đã trôi qua.
 * periodStart hiện tại + periodStart kỳ trước do caller cung cấp
 * (tháng: mùng 1 hai tháng; quý: ngày đầu quý; năm: 1/1).
 */
export const samePeriodComparison = (
  expenses: TxRecord[],
  incomes: TxRecord[],
  currentStart: Date,
  previousStart: Date,
  now: Date = new Date()
) => {
  const rawElapsed = Math.max(
    1,
    Math.floor((now.getTime() - currentStart.getTime()) / 86400000) + 1
  );
  // KẸP theo độ dài thật của kỳ TRƯỚC (vd. T2 28 ngày so với T3 31 ngày) —
  // nếu không, cửa sổ "kỳ trước" tràn qua ranh giới currentStart, mượn vài
  // ngày đầu kỳ HIỆN TẠI rồi gán nhầm cho kỳ trước (lỗi phát hiện khi audit
  // GA3: T3 31 ngày so T2 28 ngày → 3 ngày đầu T3 bị tính vào "tháng trước")
  const prevPeriodLength = Math.round(
    (currentStart.getTime() - previousStart.getTime()) / 86400000
  );
  const elapsedDays = prevPeriodLength > 0 ? Math.min(rawElapsed, prevPeriodLength) : rawElapsed;
  return {
    elapsedDays,
    current: {
      income: sumSameDays(incomes.filter(isIncome), currentStart, elapsedDays),
      expense: sumSameDays(expenses, currentStart, elapsedDays, true)
    },
    previous: {
      income: sumSameDays(incomes.filter(isIncome), previousStart, elapsedDays),
      expense: sumSameDays(expenses, previousStart, elapsedDays, true)
    }
  };
};
